/**
 * Sonara Subscriptions JavaScript SDK
 * Version 1.0.0
 */

class SonaraSubscriptions {
  /**
   * Initialize the Sonara Subscriptions client
   * @param {Object} config Configuration object
   * @param {string} config.environment 'production' or 'staging'
   * @param {string} [config.customDomain] Optional custom domain
   */
  constructor(config) {
    this.environment = config.environment || 'production';
    this.customDomain = config.customDomain;
    this.baseUrl = this._getBaseUrl();
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get base URL based on environment
   * @private
   */
  _getBaseUrl() {
    if (this.customDomain) {
      return this.customDomain;
    }

    // const isStg = window.location.host.includes('stg.');

    // return !isStg
    //   ? 'https://subtrack.appycnt.com'
    //   : 'https://subtrack-stg.appycnt.com';

    return 'https://subtrack.appycnt.com';
  }

  /** Get Init Session URL on environment **/
  getInitSessionURL() {
    return this._getBaseUrl() + '/init/session';
  }

  /**
   * Make API request
   * @private
   */
  async _request(endpoint, method = 'GET', data = null, url = this.baseUrl) {
    const options = {
      method,
      headers: this.defaultHeaders,
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(`${url}${endpoint}`, options);
      const responseData = await response.json();

      // if (!response.ok) {
      //     throw new Error(responseData.error || 'An error occurred');
      // }

      return responseData;
    } catch (error) {
      // Обработка сетевых ошибок и ошибок парсинга
      // Важно: не выбрасывать ошибки от Sentry или других сторонних сервисов
      console.error('[Sonara SDK] Request error:', error);

      // Если это сетевая ошибка, возвращаем объект с ошибкой
      return {
        error: error.message || 'Network request failed',
        statusCode: error.statusCode || null,
      };
    }
  }

  /**
   * Create subscription without user (card payment)
   * @param {Object} params Subscription parameters
   * @param {string} params.plan_id Plan ID
   * @param {string} params.email Customer email
   * @param {Object} [params.attribution] Attribution data
   * @param {Object} [params.card] Card details
   * @param {Object} [params.address] Billing address
   * @param {string} [params.currency] Currency
   */
  async createSubscriptionNoUser(params) {
    return this._request('/subs/create-no-user', 'POST', {
      plan_id: params.plan_id,
      email: params.email,
      attribution: params.attribution,
      card: params.card,
      address: params.address,
      currency: params.currency,
    });
  }

  /**
   * Create subscription without user (card payment)
   * @param {Object} params Subscription parameters
   * @param {string} params.plan_id Plan ID
   * @param {string} params.email Customer email
   * @param {Object} params.instrument Tokenize result data
   * @param {Object} [params.attribution] Attribution data
   * @param {Object} [params.address] Billing address
   * @param {string} [params.currency] Currency
   */
  async createSubscriptionNoUserPayrails(params) {
    return this._request('/subs/create-no-user', 'POST', {
      plan_id: params.plan_id,
      email: params.email,
      attribution: params.attribution,
      instrument: params.instrument,
      address: params.address,
      currency: params.currency,
    });
  }

  /**
   * Create subscription by main sub id without user
   * @param {Object} params Subscription parameters
   * @param {string} params.plan_id Plan ID
   * @param {string} params.email Customer email
   * @param {string} params.subscription_id Main plan subscriptoin id
   * @param {Object} [params.attribution] Attribution data
   * @param {Object} [params.address] Billing address
   * @param {string} [params.currency] Currency
   */
  async createSubscriptionNoUserWithSubId(params) {
    return this._request('/subs/create-no-user', 'POST', {
      plan_id: params.plan_id,
      email: params.email,
      subscription_id: params.subscription_id,
      attribution: params.attribution,
      currency: params.currency,
    });
  }

  /**
   * Create subscription without user (Apple Pay)
   * @param {Object} params Subscription parameters
   * @param {string} params.plan_id Plan ID
   * @param {string} params.email Customer email
   * @param {Object} params.token Apple Pay token
   * @param {Object} [params.attribution] Attribution data
   * @param {Object} [params.address] Billing address
   * @param {string} params.display_name Display name
   * @param {string | null} params.vault Vault system "enum": ["payrails", "enigma", null]
   * @param {string} [params.currency] Currency
   */
  async createSubscriptionNoUserApplePay(params) {
    return this._request('/subs/create-no-user-applepay', 'POST', {
      plan_id: params.plan_id,
      email: params.email,
      token: params.token,
      attribution: params.attribution,
      address: params.address,
      display_name: params.display_name,
      vault: params.vault,
      currency: params.currency,
    });
  }

  /**
   * Create subscription with user
   * @param {Object} params Subscription parameters
   * @param {string} params.plan_id Plan ID
   * @param {string} params.email Customer email
   * @param {string} [params.firebase_id] Firebase user ID
   * @param {string} [params.apple_token] Apple authentication token
   * @param {Object} [params.card] Card details (optional if using saved payment method)
   * @param {Object} [params.attribution] Attribution data
   * @param {string} [params.currency] Currency
   */
  async createSubscription(params) {
    return this._request('/subs/create', 'POST', {
      plan_id: params.plan_id,
      email: params.email,
      firebase_id: params.firebase_id,
      apple_token: params.apple_token,
      card: params.card,
      attribution: params.attribution,
      currency: params.currency,
    });
  }

  /**
   * Create subscription with Apple Pay
   * @param {Object} params Subscription parameters
   * @param {string} params.plan_id Plan ID
   * @param {string} params.email Customer email
   * @param {string} params.firebase_id Firebase user ID
   * @param {Object} params.token Apple Pay token
   * @param {Object} [params.attribution] Attribution data
   * @param {Object} [params.address] Billing address
   */
  async createSubscriptionApplePay(params) {
    return this._request('/subs/create-applepay', 'POST', {
      plan_id: params.plan_id,
      email: params.email,
      firebase_id: params.firebase_id,
      token: params.token,
      attribution: params.attribution,
      address: params.address,
    });
  }

  /**
   * Finalize (activate) subscription
   * @param {Object} params Finalization parameters
   * @param {string} params.subscription_id Subscription ID
   * @param {string} params.email Customer email
   * @param {string | undefined} params.firebase_id Firebase user ID
   * @param {string | undefined} params.apple_token Apple authentication token
   * @param {Object} [params.attribution] Attribution data
   * @param {string} [params.plan] Plan ID
   */
  async finalizeSubscription(params) {
    return this._request('/subs/finalize', 'POST', {
      subscription_id: params.subscription_id,
      email: params.email,
      firebase_id: params.firebase_id,
      apple_token: params.apple_token,
      attribution: params.attribution,
      plan: params.plan,
    });
  }

  /**
   * Get subscriptions
   * @param {string} id Subscription ID or sign-in token
   */
  async getSubscriptions(id) {
    return this._request(`/rec/subscriptions/${id}`, 'POST');
  }

  /**
   * Initialize Apple Pay session
   * @param {Object} params Payment parameters
   * @param {number} params.amount Payment amount
   * @param {string} params.label Payment label
   * @param {string} params.currency Currency code (e.g. 'USD')
   * @param {string} params.paymentDescription A description of the recurring payment that Apple Pay displays to the user in the payment sheet.
   * @param {string} params.managementURL A URL to a web page where the user can update or delete the payment method for the recurring payment.
   * @returns {Promise<ApplePaySession>}
   */
  async initApplePaySession(params) {
    if (!window.ApplePaySession) {
      throw new Error('Apple Pay is not available');
    }

    const request = {
      countryCode: 'US',
      currencyCode: params.currency,
      supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
      merchantCapabilities: ['supports3DS'],
      total: {
        label: params.label,
        amount: params.amount.toString(),
      },
      requiredBillingContactFields: ['email', 'name', 'phone', 'postalAddress'],
      recurringPaymentRequest: {
        paymentDescription: params.paymentDescription,
        managementURL: params.managementURL,
        regularBilling: {
          label: params.label,
          amount: params.amount.toString(),
          paymentTiming: 'recurring',
          recurringPaymentIntervalCount: 7,
          recurringPaymentIntervalUnit: 'day',
        },
      },
    };

    const session = new window.ApplePaySession(3, request);

    // Защита от двойного вызова completeMerchantValidation
    let merchantValidationCompleted = false;
    let validationInProgress = false;
    const validationStartTime = Date.now();

    session.onvalidatemerchant = async event => {
      const timestamp = Date.now() - validationStartTime;
      console.log(`[Apple Pay][${timestamp}ms] onvalidatemerchant triggered`, {
        validationURL: event.validationURL,
        domain: window.location.hostname,
        validationInProgress,
        merchantValidationCompleted,
      });

      if (merchantValidationCompleted) {
        console.warn(
          `[Apple Pay][${timestamp}ms] Merchant validation already completed, ignoring duplicate call`,
        );
        return;
      }

      if (validationInProgress) {
        console.warn(
          `[Apple Pay][${timestamp}ms] Validation already in progress, ignoring duplicate call`,
        );
        return;
      }

      validationInProgress = true;

      // Таймаут для валидации (Apple Pay обычно дает 3-5 секунд)
      // const validationTimeout = setTimeout(() => {
      //   if (!merchantValidationCompleted) {
      //     console.error(
      //       '[Apple Pay] Merchant validation timeout (5s exceeded)',
      //     );
      //     session.abort();
      //   }
      // }, 5000);

      try {
        const requestStartTime = Date.now();
        console.log(
          `[Apple Pay][${timestamp}ms] Sending validation request to server...`,
          {
            url: 'https://s.mytraits.ai/api/apple-pay/validate-merchant',
            validationURL: event.validationURL,
            domain: window.location.hostname,
          },
        );

        const merchantSession = await this._request(
          '/api/apple-pay/validate-merchant',
          'POST',
          {
            validationURL: event.validationURL,
            domain: window.location.hostname,
          },
          'https://s.mytraits.ai',
        );

        const requestDuration = Date.now() - requestStartTime;
        console.log(
          `[Apple Pay][${Date.now() - validationStartTime}ms] Merchant session received from server (took ${requestDuration}ms):`,
          {
            hasData: !!merchantSession,
            dataType: typeof merchantSession,
            keys: merchantSession ? Object.keys(merchantSession) : [],
            hasError: !!merchantSession?.error,
            statusCode: merchantSession?.statusCode,
          },
        );

        // Проверка на ошибку от сервера
        // Важно: различаем ошибки от Sentry (429) и реальные ошибки валидации
        const hasSentryError =
          merchantSession?.error &&
          (String(merchantSession.error).includes('429') ||
            String(merchantSession.error).toLowerCase().includes('sentry') ||
            String(merchantSession.error)
              .toLowerCase()
              .includes('too many requests'));

        if (merchantSession?.error && !hasSentryError) {
          // Реальная ошибка от сервера валидации
          const errorMessage = merchantSession.error;
          console.error(
            `[Apple Pay][${Date.now() - validationStartTime}ms] Server returned error:`,
            {
              error: merchantSession.error,
              statusCode: merchantSession.statusCode,
              message: merchantSession.message,
            },
          );
          throw new Error(errorMessage);
        }

        if (hasSentryError) {
          console.warn(
            `[Apple Pay][${Date.now() - validationStartTime}ms] Ignoring Sentry 429 error, checking for valid merchant session data...`,
          );
          // Продолжаем проверку данных, игнорируя ошибку Sentry
        }

        // Проверка что merchantSession это объект, а не строка
        let sessionData = merchantSession;

        if (typeof merchantSession === 'string') {
          try {
            sessionData = JSON.parse(merchantSession);
            console.log(
              `[Apple Pay][${Date.now() - validationStartTime}ms] Parsed string response to object`,
            );
          } catch (parseError) {
            console.error(
              '[Apple Pay] Failed to parse merchant session string:',
              parseError,
            );
            throw new Error('Invalid merchant session format (not valid JSON)');
          }
        }

        // Валидация структуры ответа
        if (!sessionData || typeof sessionData !== 'object') {
          throw new Error('Invalid merchant session (not an object)');
        }

        // Проверка обязательных полей
        const requiredFields = [
          'epochTimestamp',
          'expiresAt',
          'merchantSessionIdentifier',
        ];
        const missingFields = requiredFields.filter(
          field => !(field in sessionData),
        );

        if (missingFields.length > 0) {
          console.warn(
            '[Apple Pay] Merchant session missing fields:',
            missingFields,
          );
        }

        console.log(
          `[Apple Pay][${Date.now() - validationStartTime}ms] Calling completeMerchantValidation...`,
        );

        // Используем синхронную обработку для избежания race conditions
        merchantValidationCompleted = true;
        // clearTimeout(validationTimeout);

        session.completeMerchantValidation(sessionData);
        console.log(
          `[Apple Pay][${Date.now() - validationStartTime}ms] completeMerchantValidation called successfully`,
        );
      } catch (error) {
        // clearTimeout(validationTimeout);
        const errorTimestamp = Date.now() - validationStartTime;
        console.error(
          `[Apple Pay][${errorTimestamp}ms] Error validating merchant:`,
          {
            error,
            message: error?.message,
            stack: error?.stack,
            response: error?.response,
          },
        );
        merchantValidationCompleted = false;
        validationInProgress = false;
        session.abort();
      }
    };

    session.onpaymentauthorized = async event => {
      try {
        // Return the payment token for further processing
        return event.payment.token.paymentData;
      } catch (error) {
        console.error('[Apple Pay] Error processing payment:', error);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      }
    };

    // Добавляем обработчик ошибок сессии для диагностики
    session.onerror = event => {
      console.error('[Apple Pay] Session error:', {
        event,
        type: event?.type,
        target: event?.target,
        message: event?.message,
      });
    };

    return session;
  }

  /**
   * Validate card number using Luhn algorithm
   * @param {string} cardNumber Card number to validate
   * @returns {boolean}
   */
  validateCard(cardNumber) {
    const digits = cardNumber.replace(/\D/g, '');
    let sum = 0;
    let isEven = false;

    for (let i = digits.length - 1; i >= 0; i--) {
      let digit = parseInt(digits[i], 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  /**
   * Format card number with spaces
   * @param {string} cardNumber Raw card number
   * @returns {string} Formatted card number
   */
  formatCardNumber(cardNumber) {
    const digits = cardNumber.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g) || [];
    return groups.join(' ');
  }

  /**
   * Detect card type based on number
   * @param {string} cardNumber Card number
   * @returns {string} Card type (visa, mastercard, amex, discover, unknown)
   */
  detectCardType(cardNumber) {
    const patterns = {
      visa: /^4/,
      mastercard: /^5[1-5]/,
      amex: /^3[47]/,
      discover: /^6(?:011|5)/,
    };

    for (const [type, pattern] of Object.entries(patterns)) {
      if (pattern.test(cardNumber)) {
        return type;
      }
    }

    return 'unknown';
  }
}

// // Export for different module systems
// if (typeof module !== 'undefined' && module.exports) {
//     module.exports = SonaraSubscriptions;
// } else if (typeof define === 'function' && define.amd) {
//     define([], function() { return SonaraSubscriptions; });
// } else {
//     window.SonaraSubscriptions = SonaraSubscriptions;
// }

export default SonaraSubscriptions;
